<template>
  <component :is="layout">
    <router-view />
  </component>
  <!-- <NewUserModal v-if="showModal" :show="showModal" @close="handleCloseModal" @save="handleSaveModal" /> -->
  <SessionExpiredModal v-if="sessionExpired" :message="sessionExpiredMessage" />
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import NewUserModal from '@/components/modals/firstTimeModal.vue';
import SessionExpiredModal from '@/components/modals/SessionExpiredModal.vue';
import { sessionExpired, sessionExpiredMessage } from '@/services/errorServices/sessionExpired';
import { io } from 'socket.io-client';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { applyScheme, getSavedScheme, getSystemScheme, initSystemSchemeListener } from '@/helpers/colorSchemeHelpers.js';
import { getUserFriendlyErrorMessage } from '@/helpers/errorHandlerHelper';
import { enableScroll } from '@/helpers/scrollControlHelper.js';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { setAnalyticsUser, registerMaintenanceServiceWorker } from '@/services/firebase';
import { showNotification } from '@/plugins/notification';
import FullPageLoader from '@/components/FullPageLoader.vue';

import defaultLayout from './layouts/default.vue';
import guestLayout from './layouts/guest.vue';
const store = useStore();
const logOut = payload => store.dispatch('logOut', payload);
const addAuthStatus = status => store.dispatch('addAuthStatus', status);
const getReportPost = id => store.dispatch('fetchReportBotId', id);
const handleSetProfile = data => store.dispatch('fetchCurrentProfileOrg', data);
const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
};
const userdata = getStoredUser();
const user = computed(() => store.getters.getUser);
const isUserAuth = computed(() => store.getters.getAuthStatus);

const route = useRoute();
const router = useRouter();
// --------------------
// Modal state
// --------------------s
const showModal = ref(false);
const userId = computed(() => store.getters.getUser?.id);

const token = computed(() => store.getters.getToken);

const layout = computed(() => {
  const layout = route.meta.layout;
  return layout === 'default' ? defaultLayout : guestLayout;
});

// Global route loader visibility from store
const isRouteLoading = computed(() => store.getters.getIsRouteLoading);


const { initPushNotifications, listenForForegroundMessages } = usePushNotifications();

let unregisterSystemSchemeListener;
let authenticatedServicesStarted = false;
let pushNotificationsStarted = false;
let socketStarted = false;
const ENABLE_SOCKET = String(import.meta.env.VITE_ENABLE_SOCKET || '').toLowerCase() === 'true';
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '/';
const unreadRefreshRetryConfig = {
  attempts: 3,
  initialDelay: 250,
  multiplier: 2,
  maxDelay: 2000,
};
// --------------------
// Show modal if user is new
// --------------------
const checkPopupStatus = async () => {
  if (!userId.value) return;

  try {
    const res = await fetch(`/api/users/${userId.value}/popup-status`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token.value}`
      }
    });
    const data = await res.json(); // { hasSeenPopup: boolean }
    if (data?.hasSeenPopup === false) {
      showModal.value = true;
    }
  } catch (err) {
    console.error("Popup status check failed:", err);
  }
};
// --------------------
// Close modal
// --------------------
const handleCloseModal = () => {
  showModal.value = false;
};
// Close modal
const handleSaveModal = async () => {
  try {
    const res = await fetch(`/api/users/${userId.value}/popup-seen`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token.value}`
      }
    });

    if (!res.ok) throw new Error("Failed to update popup status");

    showModal.value = false; // hide popup
  } catch (err) {
    console.error("Error saving popup state:", err);
  }
};

const wait = delay => new Promise(resolve => setTimeout(resolve, delay));

const getUnreadRefreshBackoffDelay = attempt => {
  return Math.min(
    unreadRefreshRetryConfig.initialDelay * (unreadRefreshRetryConfig.multiplier ** attempt),
    unreadRefreshRetryConfig.maxDelay,
  );
};

const refreshUnreadNotificationsWithRetry = async () => {
  for (let attempt = 0; attempt < unreadRefreshRetryConfig.attempts; attempt += 1) {
    await wait(getUnreadRefreshBackoffDelay(attempt));
    await store.dispatch('fetchUnreadNotificationsCount');
  }
};

const useSocketIO = () => {
  const socket = io(SOCKET_URL, {
    transports: ['websocket'],
  });

  socket.on('connect', () => {
    // console.log('✅ WebSocket connected successfully');
  });

  socket.on('disconnect', () => {
    // console.log('❌ WebSocket disconnected');
    // Could show a subtle notification to user about limited functionality
  });

  socket.on('connect_error', (error) => {
    const friendlyMessage = getUserFriendlyErrorMessage(error);
    console.error('❌ WebSocket connection error:', friendlyMessage);

    // In development, also log the original error for debugging
    if (import.meta.env.MODE === 'development') {
      console.error('Original WebSocket error:', error);
    }
  });

  socket.on('post_marked_bot', payload => {
    getReportPost(payload.postId);
  });

  socket.on('new_notification', async () => {
    // console.log('🔔 New notification received via WebSocket');
    window.dispatchEvent(new CustomEvent('notifications:received', {
      detail: { source: 'socket' },
    }));

    // Fetch updated unread notifications count with retries because the socket
    // can arrive just before the notification row is readable from the API.
    await refreshUnreadNotificationsWithRetry();

    // Optionally fetch new unread notifications to update the list
    // Only do this if user is on notifications page to avoid unnecessary API calls
    const currentRoute = route.path;
    if (currentRoute === '/notifications') {
      // console.log('📱 User is on notifications page, refreshing notifications list');
      await Promise.all([
        store.dispatch('fetchNotifications', { read: false, limit: 10, offset: 0 }),
        store.dispatch('fetchNotifications', { read: true, limit: 10, offset: 0 }),
      ]);
    }
  });
};

const startAuthenticatedAppServices = async () => {
  if (!authenticatedServicesStarted) {
    authenticatedServicesStarted = true;
    store.dispatch('startGlobalTimer');
    store.dispatch('fetchReputationCategories');
    // Attributes every Analytics event logged from here on (e.g. the
    // Donate button click) to this user, instead of an anonymous client id.
    if (userId.value) setAnalyticsUser(userId.value);
  }

  checkPopupStatus();
  store.dispatch('fetchUnreadNotificationsCount');
  // initMessages only wires up the real-time socket listeners here (needed
  // app-wide, e.g. a new-message socket event while on another page) — it no
  // longer fetches the conversation list itself; that only happens while the
  // Messages section is mounted (see Messages/index.vue's
  // startConversationsPolling). The unread-messages badge is populated by
  // the dedicated, lightweight fetchUnreadMessagesCount call below instead,
  // same as fetchUnreadNotificationsCount above — the Leftbar badge should
  // show the count immediately at boot, not only after the user has clicked
  // into Messages once.
  store.dispatch('initMessages');
  store.dispatch('fetchUnreadMessagesCount');

  if (!pushNotificationsStarted) {
    pushNotificationsStarted = true;
    try {
      await initPushNotifications();
      listenForForegroundMessages();
    } catch (error) {
      pushNotificationsStarted = false;
      console.warn('[FCM] Push notification setup failed:', error);
    }
  }

  if (ENABLE_SOCKET && !socketStarted) {
    socketStarted = true;
    useSocketIO();
  }
};

const handleUserLoggedIn = () => {
  setTimeout(() => {
    startAuthenticatedAppServices();
  }, 400);
};

const handleOffline = () => {
  showNotification(
    'warn',
    "You're offline",
    'Some actions may fail until your connection is restored.',
    7000
  );
};

const handleOnline = () => {
  showNotification(
    'success',
    'Back online',
    'Reconnected. Updating latest data.',
    3000
  );

  if (isUserAuth.value) {
    store.dispatch('fetchUnreadNotificationsCount');
  }
};

window.addEventListener("user-logged-in", handleUserLoggedIn);

onMounted(async () => {
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  if (!navigator.onLine) {
    handleOffline();
  }

  // Registered unconditionally (not gated behind auth like the push-notification
  // flow below) so guests and logged-out visitors also get the maintenance-page
  // fallback worker installed. This is a separate SW file/scope from the
  // Firebase one registered later in startAuthenticatedAppServices — see
  // registerMaintenanceServiceWorker in services/firebase.js for why.
  if ('serviceWorker' in navigator) {
    registerMaintenanceServiceWorker().catch(() => {});
  }

  try {
    // main.js calls app.mount() without awaiting router.isReady(), so on a
    // fresh load `route.name`/`route.path` can still be unresolved at this
    // point. Wait for the initial navigation to settle before reading them,
    // otherwise the pre-auth checks below can miss and fire getMe anyway.
    await router.isReady();

    // window.location.pathname is used (not just route.path) as a
    // belt-and-suspenders check — it's accurate immediately, independent of
    // router-internal resolution timing.
    const currentPath = window.location.pathname;
    const preAuthPaths = [
      '/sign-in',
      '/sign-up',
      '/siwf/callback',
      '/onboarding/verification-code',
      '/onboarding/create-password',
      '/onboarding/add-avatar',
    ];
    const preAuthRouteNames = [
      'sign-in',
      'signup',
      'siwf-callback',
      'verification-code',
      'create-password',
      'add-avatar',
    ];
    // Skip initial getMe call if we're on SIWF callback route
    // The callback will handle authentication and user data loading
    const isSiwfCallback = route.path === '/siwf/callback' || route.name === 'siwf-callback';
    // Skip on sign-in (also covers post-logout, since logOut redirects here
    // via a full page reload that remounts App.vue) and the pre-auth
    // signup/onboarding flow, where there's no session yet.
    const isPreAuthRoute =
      preAuthPaths.includes(currentPath) ||
      preAuthRouteNames.includes(String(route.name)) ||
      preAuthPaths.includes(route.path);

    if (!isSiwfCallback && !isPreAuthRoute) {

      let userdata = await store.dispatch('getMe');
      handleSetProfile(userdata);
      applyScheme(getSavedScheme() || getSystemScheme());
      unregisterSystemSchemeListener = initSystemSchemeListener();

      // If user is authenticated, set up notifications and WebSocket
      if (userdata) {
        await startAuthenticatedAppServices();
      }
    } else {
      // For SIWF callback / sign-in / signup onboarding, just set up the color scheme
      applyScheme(getSavedScheme() || getSystemScheme());
      unregisterSystemSchemeListener = initSystemSchemeListener();
      // console.log('Skipping getMe call - SIWF callback or pre-auth route in progress');
    }
  } catch (error) {
    console.warn(error);
  }
});

onUnmounted(() => {
  window.removeEventListener("user-logged-in", handleUserLoggedIn);
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);
  if (unregisterSystemSchemeListener) {
    unregisterSystemSchemeListener();
  }
});
// --------------------
// On Mounted
// --------------------

// Ensure body scroll state is sane when navigating to guest pages (e.g., /welcome, /sign-in)
router.afterEach((to) => {
  if (to?.meta && to.meta.layout === 'guest') {
    try {
      enableScroll();
      document.body.classList.remove('modal-open');
      document.body.classList.remove('reactions-modal-open');
      document.body.style.removeProperty('--scrollbar-width');
      // Clear any lingering overflow styles that might interfere with BaseModal
      document.body.style.removeProperty('overflow');
      delete document.body.dataset.prevOverflow;
      // Also clean up root-level modal classes
      document.documentElement.classList.remove('modal-open-root');
    } catch { }
  }
});
</script>

<!-- OLD: <style> — plain CSS can't compile the &__ SASS-suffix nesting below -->
<style lang="scss">
/* Global layout variables for consistent sticky/fixed offsets */
:root {
  --size-header-height: 80px;
  --bottom-nav-height: 64px;
  /* mobile bottom nav */
}

/* Prevent scroll chaining to the browser window for all main content scroll containers */
.posts-scroll-container,
.centerbar-posts {
  overscroll-behavior: contain;
  /* When container hits top/bottom, don't scroll the page */
}

@media (max-width: 639px) {

  .centerbar-body,
  .primary__content,
  .stable-router-view {
    /* Add bottom padding equal to nav height + safe-area inset */
    padding-bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px));
  }

  /* Ensure scrollable content containers also account for the fixed bottom nav */
  .posts-scroll-container,
  .centerbar-posts {
    /*  padding-bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px) + 16px); */
  }
}
/* 
// background: #001871;
// color: #fff; */
</style>
